import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import PaymentModal from './PaymentModal';
import { DEFAULT_PRICING_PLANS, PricingPlan } from '../utils/pricingPlans';
import { PlanTierIcon } from './icons/PricingIcons';
import { DedicatedServerIcon } from './icons/OfferModalIcons';
import './Dashboard.css';
import './CrackerController.css';
import './NewLicenseOfferModal.css';

interface NewLicenseOfferModalProps {
  isOpen: boolean;
  onClose: () => void;
  telegramUsername?: string;
}

const NewLicenseOfferModal: React.FC<NewLicenseOfferModalProps> = ({
  isOpen,
  onClose,
  telegramUsername,
}) => {
  const [selectedPlan, setSelectedPlan] = useState<PricingPlan | null>(null);

  const handlePlanSelect = (plan: PricingPlan) => {
    setSelectedPlan(plan);
  };

  const handlePaymentClose = () => {
    setSelectedPlan(null);
  };

  const handlePaymentSuccess = () => {
    setSelectedPlan(null);
    onClose();
  };

  if (!isOpen && !selectedPlan) return null;

  return (
    <>
      <AnimatePresence>
        {isOpen && !selectedPlan && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="dashboard-modal-overlay nlo-overlay fixed inset-0 z-[55] flex justify-center p-4"
            onClick={onClose}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 12 }}
              transition={{ duration: 0.2 }}
              className="dashboard-modal-panel nlo-panel relative"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={onClose}
                className="dashboard-btn dashboard-btn--ghost nlo-close p-2"
                aria-label="Close"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <div className="cracker-panel nlo-panel-inner">
                <div className="cracker-panel-header nlo-header pr-12">
                  <div className="cracker-panel-title">
                    <div className="nlo-header-icon" role="img" aria-label="Serveur dédié">
                      <DedicatedServerIcon size={32} />
                    </div>
                    <div className="min-w-0">
                      <p className="cracker-label">Licence New</p>
                      <h2 className="cracker-heading">No dedicated server</h2>
                      <p className="cracker-subheading hidden sm:block">
                        Your n&apos;a pas encore de serveur dédié branché. Choisissez une offre pour
                        activer votre infrastructure.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="nlo-body">
                  <p className="cracker-subheading sm:hidden text-sm mb-4">
                    Your n&apos;a pas encore de serveur dédié branché. Choisissez une offre ci-dessous.
                  </p>

                  <div className="nlo-plans-grid nlo-plans-grid--single">
                    {DEFAULT_PRICING_PLANS.map((plan) => (
                        <button
                          key={plan.planId}
                          type="button"
                          className="nlo-plan-card nlo-plan-card--popular"
                          onClick={() => handlePlanSelect(plan)}
                        >
                          <span className="nlo-plan-badge">Atlas Pass</span>
                          <div className="nlo-plan-tier">
                            <PlanTierIcon tier="shell" className="w-7 h-7 shrink-0" />
                            <div>
                              <p className="nlo-plan-name">{plan.name}</p>
                              <p className="text-[0.625rem] text-[#525252] uppercase tracking-wider">
                                30-day lane
                              </p>
                            </div>
                          </div>
                          <p className="nlo-plan-price">{plan.price}</p>
                          <p className="nlo-plan-period">{plan.period}</p>
                          {plan.description && <p className="nlo-plan-desc">{plan.description}</p>}
                          <span className="nlo-plan-cta">Unlock Atlas Pass →</span>
                        </button>
                      ))}
                  </div>

                  <p className="nlo-footer-hint">
                    Secure crypto checkout • Access delivered on Telegram
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {selectedPlan && (
        <PaymentModal
          plan={selectedPlan}
          onClose={handlePaymentClose}
          onSuccess={handlePaymentSuccess}
          telegramUsername={telegramUsername}
        />
      )}
    </>
  );
};

export default NewLicenseOfferModal;
